import { inject } from "@angular/core";
import { CanMatchFn, Route, Router, Routes, UrlSegment, UrlSegmentGroup, UrlTree } from "@angular/router";
import { isObservable, firstValueFrom } from "rxjs";

/**
 * Case-insensitive route paths (work item 925).
 *
 * The Angular router matches paths case-sensitively, so a pasted /Contracts/{id} fell through to
 * the not-found page. Rather than lower-casing the URL (IDs, wallet codes and ID numbers are case
 * sensitive), a miss is matched again against the route config ignoring the case of the declared
 * path segments only, and the router is sent to the declared spelling. Parameter values, matrix
 * parameters, the query string and the fragment are carried over untouched.
 */

/** Resolves a lazy `loadChildren` to its routes; returns undefined for anything that is not a routes array. */
async function lazyChildren(route: Route): Promise<Routes | undefined> {
  if (!route.loadChildren) {
    return undefined;
  }
  let loaded: unknown = route.loadChildren();
  if (isObservable(loaded)) {
    loaded = await firstValueFrom(loaded);
  }
  loaded = await loaded;
  if (loaded && typeof loaded === "object" && "default" in loaded) {
    loaded = (loaded as { default: unknown }).default;
  }
  // An NgModule would need instantiating to read its ROUTES; the app's lazy routes are plain arrays.
  return Array.isArray(loaded) ? (loaded as Routes) : undefined;
}

/**
 * Walks `routes` in declaration order, as the router does, comparing static segments without
 * regard to case. Returns the segment paths with static segments in their declared spelling and
 * parameter positions as given, or null when no route would match even ignoring case.
 */
export async function canonicalRoutePath(segments: string[], routes: Routes): Promise<string[] | null> {
  for (const route of routes) {
    // The wildcard is what we are rescuing from, and custom matchers have no declared spelling.
    if (route.path === undefined || route.path === "**" || route.matcher) {
      continue;
    }
    const parts = route.path === "" ? [] : route.path.split("/");
    if (parts.length > segments.length) {
      continue;
    }
    const matches = parts.every((part, i) => part.startsWith(":") || part.toLowerCase() === segments[i].toLowerCase());
    if (!matches) {
      continue;
    }
    const head = parts.map((part, i) => (part.startsWith(":") ? segments[i] : part));
    const rest = segments.slice(parts.length);
    if (rest.length === 0) {
      return head;
    }
    if (route.pathMatch === "full") {
      continue;
    }
    const children = route.children ?? (await lazyChildren(route));
    if (children) {
      const tail = await canonicalRoutePath(rest, children);
      if (tail) {
        return [...head, ...tail];
      }
    }
  }
  return null;
}

/**
 * Returns `url` with its path in the declared spelling, or null when nothing matches or the
 * spelling is already canonical (so the caller cannot redirect to the same URL forever).
 */
export async function canonicalUrlTree(url: UrlTree, routes: Routes): Promise<UrlTree | null> {
  const primary = url.root.children["primary"];
  // Named outlets are not used by the backoffice; leave anything that has them alone.
  if (!primary || Object.keys(url.root.children).length > 1 || Object.keys(primary.children).length > 0) {
    return null;
  }
  const given = primary.segments.map((s) => s.path);
  const canonical = await canonicalRoutePath(given, routes);
  if (!canonical || canonical.every((path, i) => path === given[i])) {
    return null;
  }
  const segments = primary.segments.map((s, i) => new UrlSegment(canonical[i], s.parameters));
  const root = new UrlSegmentGroup([], { primary: new UrlSegmentGroup(segments, {}) });
  return new UrlTree(root, url.queryParams, url.fragment);
}

/**
 * Guard for the `**` route. It only runs once every route has failed to match, so URLs that
 * already match are never touched; a case-only miss is redirected, anything else stays not found.
 */
export const canonicalRouteCaseGuard: CanMatchFn = async () => {
  const router = inject(Router);
  const url = router.getCurrentNavigation()?.extractedUrl;
  if (!url) {
    return true;
  }
  return (await canonicalUrlTree(url, router.config)) ?? true;
};
